
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ChartBar} from '../core/ChartBar';
import {FieldModelChartBar} from '../core/ChartBar';

/**
 * ChartBarNotify struct
 */
class ChartBarNotify {
  /**
   * Initialize struct
   * @param {!ChartBar=} chartBar
   * @constructor
   */
  constructor (chartBar = new ChartBar()) {
    this.ChartBar = chartBar
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartBarNotify}
   * @param {!ChartBarNotify} other Other struct
   * @returns {!ChartBarNotify} This struct
   */
  copy (other) {
    if (other.ChartBar != null) {
      this.ChartBar = ChartBar.fromObject(other.ChartBar)
    } else {
      this.ChartBar = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartBarNotify}
   * @returns {!ChartBarNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartBarNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartBarNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartBarNotify}
   * @param {!ChartBarNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartBarNotify)) {
      throw new TypeError('Instance of ChartBarNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartBarNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ChartBar: ((this.ChartBar != null) ? this.ChartBar : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartBarNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartBarNotify} other Object value
   * @returns {!ChartBarNotify} Created struct
   */
  static fromObject (other) {
    return new ChartBarNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartBarNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartBarNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartBarNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3006
  }
}

export { ChartBarNotify };

/**
 * Fast Binary Encoding ChartBarNotify field model
 */
class FieldModelChartBarNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ChartBar = new FieldModelChartBar(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ChartBar.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ChartBar.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartBarNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3006
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartBarNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartBarNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartBar.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartBar.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartBar.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartBarNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartBarNotify}
   * @param {!ChartBarNotify} fbeValue Default value, defaults is new ChartBarNotify()
   * @returns {!ChartBarNotify} ChartBarNotify value
   */
  get (fbeValue = new ChartBarNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartBarNotify}
   * @param {!ChartBarNotify} fbeValue ChartBarNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ChartBar.fbeSize) <= fbeStructSize) {
      fbeValue.ChartBar = this.ChartBar.get()
    } else {
      fbeValue.ChartBar = new ChartBar()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartBar.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartBarNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartBarNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartBarNotify}
   * @param {!ChartBarNotify} fbeValue ChartBarNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartBarNotify}
   * @param {ChartBarNotify} fbeValue ChartBarNotify value
   */
  setFields (fbeValue) {
    this.ChartBar.set(fbeValue.ChartBar)
  }
}

export { FieldModelChartBarNotify };

/**
 * Fast Binary Encoding ChartBarNotify model
 */
class ChartBarNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartBarNotify(this.buffer, 4)
  }

  /**
   * Get the ChartBarNotify model
   * @this {!ChartBarNotifyModel}
   * @returns {!FieldModelChartBarNotify} model ChartBarNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartBarNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartBarNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartBarNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartBarNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartBarNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartBarNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartBarNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartBarNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartBarNotifyModel}
   * @param {!ChartBarNotify} value ChartBarNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartBarNotifyModel}
   * @param {!ChartBarNotify} value ChartBarNotify value, defaults is new ChartBarNotify()
   * @return {!object} Deserialized ChartBarNotify value and its size
   */
  deserialize (value = new ChartBarNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartBarNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartBarNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartBarNotifyModel}
   * @param {!number} prev Previous ChartBarNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartBarNotifyModel };
